import { Alert, Dialog, Stack, TextField } from '@all-manager/design-system-v2';
import React from 'react';
import { usePostPrivateNote } from '../../features/privateNotes/api/usePostPrivateNote';
import { useUpdatePrivateNote } from '../../features/privateNotes/api/useUpdatePrivateNote';
import { PrivateNotesDatabase } from '../../types';
import DescriptionField from './components/DescriptionField';
import DeleteNoteModal from './DeleteNoteModal';

type Props = {
  onClose: () => void;
  note?: PrivateNotesDatabase;
};

type NoteDraft = Pick<PrivateNotesDatabase, 'title' | 'content'>;

export default function CreateUpdatePrivateNoteModal(props: Props) {
  const [isModalToDeleteOpened, setIsModalToDeleteOpened] = React.useState<boolean>(false);
  const [titleError, setTitleError] = React.useState<string | null>(null);
  const [serverError, setServerError] = React.useState<string | null>(null);

  const [newNote, setNewNote] = React.useState<NoteDraft>({
    title: props.note?.title ?? '',
    content: props.note?.content ?? '',
  });

  const { mutateAsync: postNote, isPending: isCreating } = usePostPrivateNote();
  const { mutateAsync: updateNote, isPending: isUpdating } = useUpdatePrivateNote();
  const isSaving = isCreating || isUpdating;

  async function handleSaveNote(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!newNote.title?.trim()) {
      setTitleError('Saisissez un titre pour la note.');
      return;
    }

    setServerError(null);

    try {
      if (props.note) {
        await updateNote({
          id: props.note.id,
          title: newNote.title,
          content: newNote.content,
        });
      } else {
        await postNote({
          title: newNote.title,
          content: newNote.content,
        });
      }

      props.onClose();
    } catch (err) {
      console.error('Error saving note:', err);
      setServerError("La note n'a pas pu être enregistrée. Réessayez.");
    }
  }

  return (
    <>
      <Dialog
        open
        onClose={props.onClose}
        onSubmit={handleSaveNote}
        maxWidth="sm"
        fullWidth
        title={props.note ? 'Modifier la note' : 'Nouvelle note'}
        closeLabel="Fermer"
        actions={{
          primaryAction: {
            label: props.note ? 'Enregistrer la note' : 'Créer la note',
            type: 'submit',
            loading: isSaving,
          },
          secondaryAction: {
            label: 'Annuler',
            onClick: props.onClose,
            disabled: isSaving,
          },
          tertiaryAction: props.note ? {
            label: 'Supprimer',
            onClick: () => setIsModalToDeleteOpened(true),
            disabled: isSaving,
          } : undefined,
        }}
      >
        <Stack spacing={3}>
          {serverError ? <Alert severity="error">{serverError}</Alert> : null}

          <TextField
            autoFocus
            required
            label="Titre"
            fullWidth
            value={newNote.title ?? ''}
            error={!!titleError}
            helperText={titleError}
            onChange={(e) => {
              setTitleError(null);
              setNewNote({ ...newNote, title: e.target.value });
            }}
          />

          <DescriptionField
            value={newNote.content}
            onChange={(newContent) => setNewNote({ ...newNote, content: newContent })}
            label="Contenu"
          />
        </Stack>
      </Dialog>

      {isModalToDeleteOpened && props.note ? (
        <DeleteNoteModal
          note={props.note}
          onClose={() => setIsModalToDeleteOpened(false)}
          onDeleted={() => props.onClose()}
        />
      ) : null}
    </>
  );
}
